import { DrawerDiaLog } from '../drawer-dialog';
import { SpanLoading } from '../fetch-status/span-loading';
import { Button } from '../ui/button';
import { InteractCloseIcon } from '@/client/assets';
import { useAuth } from '@/client/hooks/use-auth';
import { useDidAuthStore } from '@/client/store/did-auth-store';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Props = { open: boolean; setOpen: (open: boolean) => void };

const StepDot = ({ index, current, done }: { index: number; current: number; done: boolean }) => (
  <div className="flex items-center gap-2">
    <span
      className={cn(
        'size-7 rounded-full flex items-center justify-center text-xs font-mono font-semibold shrink-0 border',
        done && 'bg-primary text-primary-foreground border-primary',
        !done && index === current && 'border-primary text-primary',
        !done && index !== current && 'border-border text-muted-foreground'
      )}
    >
      {done ? '✓' : index + 1}
    </span>
  </div>
);

export const OnboardingWizard = ({ open, setOpen }: Props) => {
  const { t } = useTranslation();
  const { loginWallet, linkWallet, connectWallet, walletConnected, canTrade, linkedWallet } = useAuth();
  const identity = useDidAuthStore(s => s.identity);
  const dismissOnboarding = useDidAuthStore(s => s.dismissOnboarding);
  const [step, setStep] = useState(0);
  // funded/approved are user-confirmed in mock (no on-chain read); prod can wire
  // useSingerTokenBalance>0 and useClobApproval().isUsdcApproved (spec #43).
  const [funded, setFunded] = useState(false);
  const [approved, setApproved] = useState(false);

  // DID sessions must PROVE a wallet (link-wallet signature) — connectivity
  // alone isn't enough. SIWE sessions already proved theirs at login.
  const needsLink = !canTrade;
  const walletDone = canTrade && walletConnected;
  const steps = useMemo(
    () => [
      {
        title: t('ob-step1-title', 'Connect your wallet'),
        why: t('ob-step1-why', 'Your wallet holds your USDT and signs your trades — self-custody, we never hold funds.'),
        done: walletDone,
      },
      {
        title: t('ob-step2-title', 'Fund with USDT'),
        why: t('ob-step2-why', 'Send USDT to your own wallet to start trading. Keep a little BNB for gas.'),
        done: funded,
      },
      {
        title: t('ob-step3-title', 'Enable trading'),
        why: t('ob-step3-why', 'A one-time setup so the exchange can match your orders. You only do this once.'),
        done: approved,
      },
    ],
    [t, walletDone, funded, approved]
  );

  const close = () => {
    if (identity?.sub) dismissOnboarding(identity.sub);
    setOpen(false);
  };


  const body: ReactNode = (() => {
    if (step === 0) {
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{steps[0].why}</p>
          {walletDone ? (
            <div className="rounded-xl border border-border-bright bg-primary/8 px-4 py-3 text-sm text-foreground">
              {t('ob-wallet-connected', 'Wallet connected.')}
              {linkedWallet && (
                <div className="mt-1 text-xs text-muted-foreground font-mono break-all">{linkedWallet}</div>
              )}
            </div>
          ) : needsLink ? (
            // DID session: connect + SIWE signature proof binds the wallet to
            // this account (one-time). linkWallet handles the connect step.
            <button type="button" onClick={() => linkWallet?.()} className="btn-brand btn-brand w-full py-3 rounded-lg text-sm">
              {t('ob-link-wallet', 'Connect & link wallet')}
            </button>
          ) : (
            // Wallet already proven, just not connected right now.
            <button
              type="button"
              onClick={() => (connectWallet ?? loginWallet)?.()}
              className="btn-brand btn-brand w-full py-3 rounded-lg text-sm"
            >
              {t('connect-wallet', 'Connect Wallet')}
            </button>
          )}
        </div>
      );
    }
    if (step === 1) {
      return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{steps[1].why}</p>
          <div className="rounded-xl border border-border bg-secondary/60 px-4 py-3 space-y-1">
            <div className="text-xs text-muted-foreground">
              {t('ob-fund-hint', 'Make sure your connected wallet holds USDT on BNB Smart Chain. You stay in full control of your funds.')}
            </div>
            <div className="text-[11px] text-muted-foreground">{t('ob-gas-note', 'Keep a small amount of BNB for gas.')}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={() => { setFunded(true); setStep(2); }}>
              {t('ob-skip', 'Skip for now')}
            </Button>
            <button type="button" className="btn-brand btn-brand flex-1 py-2 rounded-lg text-sm" onClick={() => { setFunded(true); setStep(2); }}>
              {t('ob-continue', 'Continue')}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{steps[2].why}</p>
        <button
          type="button"
          className="btn-brand btn-brand w-full py-3 rounded-lg text-sm"
          onClick={() => {
            setApproved(true);
            toast.success(t('ob-ready', "You're all set — happy trading!"));
            close();
          }}
        >
          {t('ob-enable', 'Enable trading')}
        </button>
      </div>
    );
  })();

  return (
    <DrawerDiaLog open={open} setOpen={setOpen} className="w-full md:max-w-md border border-foreground/4">
      <div className="px-4 pb-6 md:p-5 space-y-5">
        <div className="flex items-center justify-between">
          <dl className="space-y-1">
            <dt className="text-lg leading-6 font-display font-semibold text-foreground">{t('ob-title', 'Get ready to trade')}</dt>
            <dd className="text-xs text-muted-foreground">{t('ob-subtitle', 'Three quick steps')}</dd>
          </dl>
          <Button size="icon" variant="ghost" className="hover:scale-90 transition" onClick={close}>
            <InteractCloseIcon className="!size-6 fill-foreground/40" />
          </Button>
        </div>

        {/* step indicator */}
        <div className="flex items-center justify-between px-1">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <StepDot index={i} current={step} done={s.done} />
              {i < steps.length - 1 && <span className={cn('flex-1 h-px mx-2', s.done ? 'bg-primary' : 'bg-border')} />}
            </div>
          ))}
        </div>

        <div>
          <div className="text-sm font-semibold text-foreground mb-2">{steps[step].title}</div>
          {body}
        </div>

        {/* footer nav */}
        <div className="flex items-center justify-between pt-1">
          <Button variant="ghost" size="sm" disabled={step === 0} onClick={() => setStep(s => Math.max(0, s - 1))} className="text-xs text-muted-foreground">
            {t('ob-back', 'Back')}
          </Button>
          {step === 0 && (
            <Button
              variant="ghost"
              size="sm"
              disabled={!walletDone}
              onClick={() => setStep(1)}
              className="text-xs text-primary disabled:text-muted-foreground"
            >
              {walletDone ? t('ob-next', 'Next') : <SpanLoading />}
            </Button>
          )}
        </div>
      </div>
    </DrawerDiaLog>
  );
};

export default OnboardingWizard;
